import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon as MoonIcon, FileText, ScanLine, Brain, TrendingUp, ArrowRight } from "lucide-react";
import Starfield from "@/components/Starfield";
import Moon from "@/components/Moon";

const FEATURES = [
  { icon: FileText, title: "PDF Summarizer", desc: "Drop a PDF and get a clean, structured summary in seconds." },
  { icon: ScanLine, title: "Question Scanner", desc: "Paste any lesson or problem and understand it fast." },
  { icon: Brain, title: "Quiz Generator", desc: "Auto-built quizzes turn reading into real recall." },
  { icon: TrendingUp, title: "Progress Tracking", desc: "Honest accuracy by subject — shown as moon phases." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" } }),
};

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#06080d] text-white">
      <Starfield count={90} />
      <div className="pointer-events-none absolute right-[-16%] top-[11%] hidden lg:block xl:right-[-5%] 2xl:right-[2%]">
        <Moon size={540} />
      </div>

      {/* Nav */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2.5" data-testid="landing-logo">
          <MoonIcon className="h-6 w-6" strokeWidth={1.5} />
          <span className="font-sans text-lg font-semibold tracking-tight">Quizer</span>
        </div>
        <nav className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login-link" className="rounded-full px-4 py-2 text-sm text-zinc-300 transition-colors hover:text-white">
            Log In
          </Link>
          <Link to="/register" data-testid="nav-get-started-button" className="shimmer rounded-full bg-white px-5 py-2 text-sm font-semibold text-[#06080d]">
            Get Started
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24 pt-16 lg:pt-28">
        <motion.p variants={fade} custom={0} initial="hidden" animate="show" className="font-mono text-xs uppercase tracking-[0.35em] text-zinc-400">
          AI Learning Platform
        </motion.p>
        <motion.h1 variants={fade} custom={1} initial="hidden" animate="show" className="mt-6 max-w-2xl font-sans text-5xl font-light leading-[1.05] tracking-tight sm:text-6xl">
          Study Under a <span className="silver-text font-normal">Smarter Sky.</span>
        </motion.h1>
        <motion.p variants={fade} custom={2} initial="hidden" animate="show" className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
          Your lessons, summaries, and quizzes — all in one place, powered by AI.
        </motion.p>
        <motion.div variants={fade} custom={3} initial="hidden" animate="show" className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/register" data-testid="hero-get-started-button" className="shimmer flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-[#06080d]">
            Get Started <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/login" data-testid="hero-login-button" className="rounded-full border border-white/15 px-7 py-3 text-sm text-zinc-200 transition-colors hover:border-white/35 hover:text-white">
            Log In
          </Link>
        </motion.div>

        {/* Moon for mobile */}
        <div className="mt-16 flex justify-center lg:hidden">
          <Moon size={240} />
        </div>

        {/* Feature grid */}
        <div className="mt-24 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              variants={fade}
              custom={4 + i}
              initial="hidden"
              animate="show"
              data-testid={`feature-card-${i}`}
              className="glass group rounded-2xl p-6 transition-colors hover:border-white/25"
            >
              <f.icon className="h-6 w-6 text-zinc-200" strokeWidth={1.5} />
              <h3 className="mt-4 font-sans text-lg font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
